import { Claim } from '@wsphere/warranties/domain';

/** What approvals hold back, per active authorization, and why nothing can be paid when that's the case. */
export interface PaymentHolds {
  overrides: Claim.Override.Model[];
  /** Approvals that can't be reversed: money is out on their authorization. */
  paidTargetIds: string[];
  byAuthorization: Record<string, { override: Claim.Override.Model | null; reason: string | null }>;
  /** Set when every authorization with money left is held back; the first one says why. */
  reason: string | null;
}

/** Approvals holding back payment; shared by the payments page and the workspace. */
export function getPaymentHolds(
  claim: Pick<Claim.Model, 'authorizations' | 'paymentBalances'>,
  overrides: Claim.Override.Model[],
  allocations: Claim.Allocation.ListAllocationsItem[]
): PaymentHolds {
  const authorizations = Claim.Authorization.getActiveAuthorizations(claim.authorizations ?? []);
  const isBlocking = (override: Claim.Override.Model) => Claim.Override.BLOCKING_STATES.includes(override.state);

  // A split is paid all or nothing: one share waiting on an approval holds back every share of it.
  const byAuthorization: PaymentHolds['byAuthorization'] = {};
  for (const authorization of authorizations) {
    const split = allocations.filter(
      allocation =>
        allocation.authorizationId === authorization.id && allocation.status === Claim.Allocation.Status.ACTIVE
    );
    const hold = Claim.getPaymentHold(
      authorization.id,
      split.map(share => share.id),
      overrides
    );
    byAuthorization[authorization.id] = {
      override: overrides.find(override => override.targetId === authorization.id && isBlocking(override)) ?? null,
      reason: hold ? Claim.Override.heldBackMessage(hold) : null,
    };
  }

  // Authorizations with money out, and every share of them: the server refuses to reverse their approvals.
  const paidAuthorizationIds = new Set(
    allocations
      .filter(
        allocation => allocation.paymentStatus && Claim.ClaimPayment.SENT_STATUSES.includes(allocation.paymentStatus)
      )
      .map(allocation => allocation.authorizationId)
  );
  const paidTargetIds = [
    ...paidAuthorizationIds,
    ...allocations
      .filter(allocation => paidAuthorizationIds.has(allocation.authorizationId))
      .map(allocation => allocation.id),
  ];

  const reasons = (claim.paymentBalances ?? [])
    .filter(balance => balance.remaining > 0)
    .map(balance => byAuthorization[balance.authorizationId]?.reason ?? null);

  return {
    overrides,
    paidTargetIds,
    byAuthorization,
    reason: reasons.length && reasons.every(Boolean) ? reasons[0] : null,
  };
}
